import * as React from "react";
import { useLocation } from "wouter";
import { MapPin, Zap, Plus, ChevronRight, Home as HomeIcon, RefreshCw, Check, AlertTriangle, Clock, Search, FlaskConical, Target, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.js";
import { apiFetch } from "@/lib/api.js";
import { cn } from "@/lib/utils.js";
import { AdSlot } from "@/components/AdSlot.js";
import { usePlan } from "@/contexts/PlanContext.js";
import { useDashboard } from "@/contexts/DashboardContext.js";
import { useToast } from "@/components/Toast.js";
import { Tooltip } from "@/components/Tooltip.js";
import { ReorgSuggestions } from "./ReorgSuggestions.js";

export default function HomePage() {
  const [, navigate] = useLocation();
  const { user, refresh } = useAuth();
  const { usage } = usePlan();
  const { data: dashboard, refresh: refreshDashboard, loading: dashLoading } = useDashboard();
  const toast = useToast();
  const onboarding = dashboard?.onboarding ?? null;
  const [invitations, setInvitations] = React.useState(dashboard?.invitations ?? []);
  const flows = dashboard?.flows ?? [];

  React.useEffect(() => {
    if (dashboard?.invitations) setInvitations(dashboard.invitations);
  }, [dashboard]);

  const acceptInvite = async (id: string) => {
    try {
      await apiFetch(`/api/invitations/${id}/accept`, { method: "PUT" });
      setInvitations((prev) => prev.filter((i) => i.id !== id));
      toast.show("Invitation accepted!");
      refresh();
      refreshDashboard();
    } catch (e: any) { toast.show(e.message ?? "Failed to accept invitation", "error"); }
  };

  const rejectInvite = async (id: string) => {
    try {
      await apiFetch(`/api/invitations/${id}/reject`, { method: "PUT" });
      setInvitations((prev) => prev.filter((i) => i.id !== id));
      toast.show("Invitation declined");
      refreshDashboard();
    } catch (e: any) { toast.show(e.message ?? "Failed to reject invitation", "error"); }
  };

  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem('ht_onboarding_complete_dismissed') === '1');
  const [organizeNudgeDismissed, setOrganizeNudgeDismissed] = React.useState(() => localStorage.getItem('ht_organize_nudge_dismissed') === '1');
  const [goals, setGoals] = React.useState<string[]>([]);
  const [overdueTrials, setOverdueTrials] = React.useState<{ id: string; goalId: string | null; targetSpaceName: string | null; reviewAfter: string | null; startedAt: string | null; status: string }[]>([]);
  const [inWindowTrials, setInWindowTrials] = React.useState<{ id: string; goalId: string | null; targetSpaceName: string | null; reviewAfter: string | null; startedAt: string | null; status: string }[]>([]);
  // Guard against a malformed/unexpected payload: `goals` is read with
  // `.includes()` below, so it must always be an array.
  React.useEffect(() => { apiFetch<{ goals?: string[] }>("/api/onboarding/goals").then((d) => setGoals(d?.goals ?? [])).catch(() => {}); }, []);
  React.useEffect(() => {
    apiFetch<{ id: string; goalId: string | null; targetSpaceName: string | null; reviewAfter: string | null; startedAt: string | null; status: string }[]>("/api/placement-trials?active=true")
      .then((trials) => {
        const now = new Date();
        const overdue = trials.filter((t) =>
          ["confirmed_moved", "in_progress"].includes(t.status) && t.reviewAfter && new Date(t.reviewAfter) < now
        );
        const inWindow = trials.filter((t) =>
          ["confirmed_moved", "in_progress"].includes(t.status) && t.reviewAfter && new Date(t.reviewAfter) >= now
        );
        setOverdueTrials(overdue);
        setInWindowTrials(inWindow);
      })
      .catch(() => {});
  }, []);
  const spacesLabel = goals.includes("prepare_move") ? "Set up rooms for your move" : "Map out your spaces";
  const firstItemLabel = goals.includes("prepare_move") ? "Label your first item" : goals.includes("insurance_docs") ? "Document your first item" : goals.includes("track_stuff") ? "Log your first item" : "Catalog your first item";
  const onboardingTasks = [
    { label: spacesLabel, done: onboarding?.hasSpaces ?? false, path: "/spaces" },
    { label: "Define what a room is for", done: onboarding?.hasIntent ?? false, path: "/spaces" },
    { label: firstItemLabel, done: onboarding?.hasItems ?? false, path: "/inventory?action=add" },
  ];
  const progress = onboardingTasks.filter((t) => t.done).length / onboardingTasks.length;

  // Progressive disclosure: determine what to show based on activation state
  const isNewUser = onboarding && onboarding.itemCount === 0 && onboarding.spaceCount === 0;
  const hasSpacesOnly = onboarding && onboarding.hasSpaces && !onboarding.hasItems;
  const hasReachedMilestone = onboarding?.hasReachedMilestone ?? false;
  const hasIntent = onboarding?.hasIntent ?? false;

  // Post-cataloging bridge: show nudge if user has intent set but no flows yet
  const [routineBridgeDismissed, setRoutineBridgeDismissed] = React.useState(() => localStorage.getItem(`ht_routine_bridge_dismissed_${user?.id}`) === "1");
  const showRoutineBridgeNudge = hasIntent
    && !routineBridgeDismissed
    && (dashboard?.roomSpaces ?? []).length > 0
    && flows.length === 0;

  // Intent discovery nudge: show when user has spaces but hasn't set any intent yet
  const [intentHomeDismissed, setIntentHomeDismissed] = React.useState(() => localStorage.getItem(`ht_intent_home_dismissed_${user?.id}`) === "1");
  const showIntentDiscoveryNudge = hasReachedMilestone
    && !hasIntent
    && onboarding?.hasSpaces
    && (dashboard?.roomSpaces ?? []).length > 0
    && !intentHomeDismissed;

  const [searchPromptDismissed, setSearchPromptDismissed] = React.useState(() => localStorage.getItem("ht_search_prompt_dismissed") === "1");
  const showSearchPrompt = !!onboarding && onboarding.itemCount >= 3 && !hasReachedMilestone && !searchPromptDismissed;
  const showOrganizeNudge = !!onboarding && onboarding.itemCount >= 5 && !organizeNudgeDismissed;

  // Cut nudge fatigue: at most ONE contextual nudge competes for attention at a
  // time, in priority order (the next best step for where the user is now).
  // Actionable alerts (low stock / unlocated) and trial cards are NOT nudges —
  // they represent real pending work and always show.
  const activeNudge: "routineBridge" | "intentDiscovery" | "search" | "organize" | null =
    showRoutineBridgeNudge ? "routineBridge"
    : showIntentDiscoveryNudge ? "intentDiscovery"
    : showOrganizeNudge ? "organize"
    : showSearchPrompt ? "search"
    : null;

  // "At rest" calm state: the user is set up and has nothing pending — no alerts,
  // no due/active trials, no nudge, no active projects. This is the brand's
  // payoff moment (an organized home is a calm one), not an empty afterthought.
  const hasPendingAlerts = !!onboarding
    && (((dashboard?.lowStockCount ?? 0) > 0) || ((dashboard?.unlocatedCount ?? 0) > 0));
  const atRest = !!onboarding
    && !isNewUser
    && progress === 1
    && dismissed
    && !hasPendingAlerts
    && overdueTrials.length === 0
    && inWindowTrials.length === 0
    && flows.length === 0
    && activeNudge === null
    && invitations.length === 0;

  return (
    <div className="space-y-8">
      <header>
        <div className="flex items-center gap-2 mb-1">
          <HomeIcon className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">Homethyst</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 font-headline">Welcome Home</h1>
        <p className="text-gray-400 mt-1 text-sm">Manage your home with clarity and calm</p>
      </header>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="space-y-2">
          {invitations.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between p-4 rounded-lg bg-amethyst-soft border border-clay-border">
              <span className="text-sm"><span className="font-medium">{inv.invitedByName}</span> invited you to join their household</span>
              <div className="flex gap-2">
                <button onClick={() => acceptInvite(inv.id)} className="px-3 py-1 rounded-lg bg-primary text-white text-xs font-medium">Accept</button>
                <button onClick={() => rejectInvite(inv.id)} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-600 text-xs font-medium">Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* At rest — the calm payoff state: set up, nothing pending. */}
      {atRest && (
        <div className="rounded-lg border border-clay-border bg-sage-wash p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">You're all caught up</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-sm mx-auto">
            Your home is organized and nothing needs your attention right now. Enjoy the calm.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <button onClick={() => navigate("/inventory?action=add")} className="px-4 py-2 rounded-lg bg-white border border-neutral-border/40 text-gray-700 text-sm font-medium hover:border-primary/40 transition-colors flex items-center gap-1.5">
              <Plus className="w-4 h-4" />Add an item
            </button>
            <button onClick={() => navigate("/organize")} className="px-4 py-2 rounded-lg bg-white border border-neutral-border/40 text-gray-700 text-sm font-medium hover:border-primary/40 transition-colors">
              Organize a space
            </button>
          </div>
        </div>
      )}

      {/* Stats — only show once user has started adding content */}
      {onboarding && !isNewUser && (
        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-2xl bg-white border border-neutral-border/20 text-center">
            <div className="text-2xl font-bold text-primary">{onboarding.itemCount}</div>
            <div className="text-xs text-gray-400">Items</div>
          </div>
          <div className="p-4 rounded-2xl bg-white border border-neutral-border/20 text-center">
            <div className="text-2xl font-bold text-primary">{onboarding.spaceCount}</div>
            <div className="text-xs text-gray-400">Spaces</div>
          </div>
          <div className="p-4 rounded-2xl bg-white border border-neutral-border/20 text-center">
            <div className="text-2xl font-bold text-primary">{Math.round(progress * 100)}%</div>
            <div className="text-xs text-gray-400">Setup</div>
          </div>
        </div>
      )}

      {/* Organizational alerts */}
      {onboarding && (dashboard?.lowStockCount || dashboard?.unlocatedCount) ? (
        <div className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Needs attention</h2>
          {(dashboard?.lowStockCount ?? 0) > 0 && (
            <button onClick={() => navigate("/settings/notifications")} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="flex-1"><span className="text-sm font-medium text-amber-800">{dashboard!.lowStockCount} item{dashboard!.lowStockCount! > 1 ? "s" : ""} low on stock</span></div>
              <ChevronRight className="w-4 h-4 text-amber-400" />
            </button>
          )}
          {(dashboard?.unlocatedCount ?? 0) > 0 && (
            <button onClick={() => navigate("/inventory")} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-50 border border-blue-200 text-left">
              <MapPin className="w-5 h-5 text-blue-500 shrink-0" />
              <div className="flex-1"><span className="text-sm font-medium text-blue-800">{dashboard!.unlocatedCount} item{dashboard!.unlocatedCount! > 1 ? "s" : ""} without a location</span></div>
              <ChevronRight className="w-4 h-4 text-blue-400" />
            </button>
          )}

        </div>
      ) : null}

      {/* Post-cataloging bridge: nudge to set up routines for well-cataloged spaces */}
      {activeNudge === "routineBridge" && (
        <div className="bg-amethyst-soft rounded-lg border border-clay-border p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0 mt-0.5">
            <RefreshCw className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Ready for a routine?</h3>
            <p className="text-xs text-gray-500 mb-3">
              You've been cataloging well! Setting up a simple routine helps keep your spaces organized with minimal effort.
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/spaces")} className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium">
                View your spaces
              </button>
              <button onClick={() => { localStorage.setItem(`ht_routine_bridge_dismissed_${user?.id}`, "1"); setRoutineBridgeDismissed(true); }} className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gray-700">
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Intent discovery nudge: prompt users to define what their spaces are for */}
      {activeNudge === "intentDiscovery" && (
        <div className="bg-amethyst-soft rounded-lg border border-clay-border p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0 mt-0.5">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Tell us how your rooms work</h3>
            <p className="text-xs text-gray-500 mb-3">
              Define what activities happen in your spaces — this unlocks personalized routines to keep things organized.
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate(`/spaces/${dashboard?.roomSpaces?.[0]?.id}`)} className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium">
                Set up {dashboard?.roomSpaces?.[0]?.name ?? "a room"}
              </button>
              <button onClick={() => { localStorage.setItem(`ht_intent_home_dismissed_${user?.id}`, "1"); setIntentHomeDismissed(true); }} className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gray-700">
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding card */}
      {onboarding && !dismissed && (
        progress === 1 ? (
          <div className="bg-white rounded-lg border border-clay-border p-6 text-center space-y-3">
            <div className="text-3xl">🎉</div>
            <h2 className="text-lg font-bold">You're all set!</h2>
            <p className="text-sm text-gray-400">Your home is ready to manage</p>
            <button onClick={() => { localStorage.setItem('ht_onboarding_complete_dismissed', '1'); setDismissed(true); }} className="text-sm font-medium text-primary">Dismiss</button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-clay-border p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div><h2 className="text-lg font-bold">Getting Started</h2><p className="text-sm text-gray-400">Build your digital home</p></div>
              <span className="text-sm font-bold text-amethyst-ink bg-amethyst-soft px-3 py-1 rounded-full tabular-nums">{Math.round(progress * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress * 100}%` }} /></div>
            <div className="space-y-3">
              {onboardingTasks.map((task, i) => (
                <div key={i} onClick={() => !task.done && navigate(task.path)} className={cn("flex items-center gap-3", !task.done && "cursor-pointer")}>
                  <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center", task.done ? "bg-primary border-primary" : "border-gray-200")}>
                    {task.done && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className={cn("text-sm flex-1", task.done ? "text-gray-400 line-through" : "font-medium")}>{task.label}</span>
                  {!task.done && <ChevronRight className="w-4 h-4 text-gray-300" />}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Proactive search prompt — show after user has 3+ items but hasn't dismissed */}
      {activeNudge === "search" && (
        <div className="bg-white rounded-lg border border-clay-border p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
            <Search className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Try searching for something you added</p>
            <p className="text-xs text-gray-500 mt-0.5">Find any item instantly — by name, location, or tag</p>
          </div>
          <button onClick={() => { localStorage.setItem("ht_search_prompt_dismissed", "1"); setSearchPromptDismissed(true); navigate("/inventory"); }} className="text-xs text-gray-500 hover:text-gray-700 shrink-0">Dismiss</button>
        </div>
      )}

      {/* Quick actions — progressive: show only relevant actions based on user state */}
      {!isNewUser && (() => {
        const actions = [
          { icon: Plus, label: "Add Item", desc: "Log a new item", color: "bg-amethyst-soft text-amethyst-ink", path: "/inventory?action=add", tip: undefined },
          { icon: MapPin, label: "Spaces", desc: "Manage locations", color: "bg-sage-wash text-sage-deep", path: "/spaces", tip: "Organize your home into rooms, closets, and containers" },
          ...(hasReachedMilestone ? [{ icon: RefreshCw, label: "Quick Reset", desc: "Declutter a space", color: "bg-sage-wash text-sage-deep", path: "/organize", tip: undefined }] : []),
        ];
        return (
          <div className={`grid ${hasReachedMilestone ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
            {actions.map(({ icon: Icon, label, desc, color, path, tip }) => {
              const btn = (
                <button key={label} onClick={() => navigate(path)} className="flex flex-col items-start gap-2 p-5 rounded-2xl border border-neutral-border/30 bg-white hover:border-primary/30 transition-all text-left h-full w-full">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-6 h-6" /></div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">{label}</div>
                    <div className="text-xs text-gray-400">{desc}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 self-end" />
                </button>
              );
              return tip ? <Tooltip key={label} id={`home-${label.toLowerCase().replace(/ /g, "-")}`} tip={tip}>{btn}</Tooltip> : btn;
            })}
          </div>
        );
      })()}

      {/* Reorganization suggestions */}
      {hasReachedMilestone && (
        <ReorgSuggestions onNavigate={navigate} onRefreshDashboard={refreshDashboard} />
      )}

      {/* Recently added */}
      {(dashboard?.recentItems?.length ?? 0) > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-900 flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" />Recently Added</h2>
            <button onClick={() => navigate("/inventory")} className="text-xs text-primary font-medium">View all</button>
          </div>
          <div className="space-y-2">
            {dashboard!.recentItems!.slice(0, 5).map((item) => (
              <button key={item.id} onClick={() => navigate(`/inventory?highlight=${item.id}`)} className="w-full flex items-center justify-between p-3 rounded-xl bg-white border border-neutral-border/20 hover:border-primary/30 text-left">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{item.name}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {item.spaceName && <><MapPin className="w-3 h-3 inline mr-1" />{item.spaceName}</>}
                  </div>
                </div>
                <span className="text-[10px] text-gray-300 shrink-0 ml-2">{new Date(item.createdAt).toLocaleDateString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}


      {/* Organize milestone nudge — shows when user reaches 5+ items */}
      {activeNudge === "organize" && (
        <div className="bg-white rounded-lg border border-clay-border p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                You've got {onboarding?.itemCount} items tracked
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Want to try organizing one area? Set a goal and we'll help you find the best spots.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => navigate("/organize/goals")}
                  className="px-3 py-1.5 bg-primary text-white rounded-md text-xs font-medium hover:bg-amethyst-ink transition-colors"
                >
                  Try organizing
                </button>
                <button
                  onClick={() => { localStorage.setItem("ht_organize_nudge_dismissed", "1"); setOrganizeNudgeDismissed(true); }}
                  className="px-3 py-1.5 text-gray-500 text-xs font-medium hover:text-gray-700 transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-window trials — active trials not yet due for review */}
      {inWindowTrials.length > 0 && (
        <div>
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-primary" />Trying a new arrangement
          </h2>
          <div className="space-y-2">
            {inWindowTrials.slice(0, 3).map((trial) => {
              const startDate = trial.startedAt ? new Date(trial.startedAt) : null;
              const reviewDate = trial.reviewAfter ? new Date(trial.reviewAfter) : null;
              const now = new Date();
              const totalDays = startDate && reviewDate ? Math.max(1, Math.round((reviewDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))) : 14;
              const elapsedDays = startDate ? Math.max(0, Math.round((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
              const progress = Math.min(1, elapsedDays / totalDays);

              return (
                <button
                  key={trial.id}
                  onClick={() => navigate(trial.goalId ? `/organize/goals/${trial.goalId}` : "/organize/goals")}
                  className="w-full p-4 rounded-lg bg-amethyst-soft border border-clay-border text-left hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm font-medium text-gray-800">
                        Day {elapsedDays} of {totalDays}{trial.targetSpaceName ? ` — items in ${trial.targetSpaceName}` : ""}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-primary/40" />
                  </div>
                  <div className="h-1.5 bg-clay-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${Math.round(progress * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1.5">
                    How's it going so far?
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Trials needing attention */}
      {overdueTrials.length > 0 && (
        <div>
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-amber-500" />Trials needing attention
          </h2>
          <div className="space-y-2">
            {overdueTrials.slice(0, 3).map((trial) => (
              <button
                key={trial.id}
                onClick={() => navigate(trial.goalId ? `/organize/goals/${trial.goalId}` : "/organize/goals")}
                className="w-full flex items-center justify-between p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left hover:border-amber-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FlaskConical className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-amber-800">
                      Trial{trial.targetSpaceName ? ` → ${trial.targetSpaceName}` : ""} — ready for check-in
                    </div>
                    <div className="text-xs text-amber-600">
                      Review was due {new Date(trial.reviewAfter!).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active projects */}
      {flows.length > 0 && (
        <div>
          <h2 className="font-bold text-gray-900 mb-3">Active Projects</h2>
          <div className="space-y-2">
            {flows.slice(0, 3).map((flow) => (
              <button key={flow.id} onClick={() => navigate("/organize")} className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-neutral-border/20 hover:border-primary/30 text-left">
                <div>
                  <div className="font-medium text-sm">{flow.title}</div>
                  <div className="text-xs text-gray-400 capitalize">{flow.frequency} · Due {new Date(flow.nextDueDate).toLocaleDateString()}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            ))}
          </div>
        </div>
      )}

      <AdSlot slot="home-bottom" provider={usage?.adsEnabled ? "adsense" : "none"} />
    </div>
  );
}
